import React from 'react';
import { Avatar, AvatarProps } from '@mui/material';

interface ProfileAvatarProps extends Omit<AvatarProps, 'src' | 'children'> {
  imageUrl?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  name?: string | null; // Fallback for full name string
}

/**
 * ProfileAvatar Component
 * Displays user profile picture if available, otherwise shows initials
 * - Shows first letter of first name if available
 * - Falls back to first letter of full name if no first name
 * - Only displays image if URL is valid (http/https)
 */
const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  imageUrl,
  firstName,
  lastName,
  fullName,
  name,
  sx,
  ...other
}) => {
  // Get initials - prefer first name initial
  const getInitials = (): string => {
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (fullName || name) {
      const nameStr = fullName || name || '';
      const parts = nameStr.trim().split(' ');
      if (parts.length > 0 && parts[0]) {
        return parts[0].charAt(0).toUpperCase();
      }
    }
    if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }
    return '?';
  };

  // Validate URL - must be http or https
  const isValidUrl = (url: string | null | undefined): boolean => {
    if (!url || url.trim() === '') return false;
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const initials = getInitials();
  const hasValidImage = isValidUrl(imageUrl);

  const finalSx = {
    ...sx,
    ...(!hasValidImage && {
      backgroundColor: (sx as any)?.bgcolor || (sx as any)?.backgroundColor || '#1976d2',
    }),
  };

  return (
    <Avatar
      src={hasValidImage ? imageUrl || undefined : undefined}
      sx={finalSx}
      {...other}
    >
      {!hasValidImage && initials}
    </Avatar>
  );
};

export default ProfileAvatar;

